import type { components } from "../api/schema";

// The single `google.rpc.Status`-shaped error envelope, surfaced by the
// generated client with its closed domain code set (docs/04-api-contract.md
// §8; docs/09-frontend-surface.md §2). Never re-modelled by hand.
export type ApiError = components["schemas"]["Status"];
export type ApiErrorCode = ApiError["code"];

// Wraps the Status envelope in an Error so it can be thrown through TanStack
// Query without violating the throw-only-Error contract. `status` carries the
// verbatim envelope fields (`code`, `message`, `request_id`, `retryable`,
// `retry_after`).
export class ApiRequestError extends Error {
  readonly status: ApiError;

  constructor(status: ApiError) {
    super(status.message);
    this.name = "ApiRequestError";
    this.status = status;
  }
}

export function isApiRequestError(value: unknown): value is ApiRequestError {
  return value instanceof ApiRequestError;
}

export function requestErrorDescription(
  error: unknown,
  fallback = "Request failed.",
): string {
  if (isApiRequestError(error)) {
    return error.status.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return fallback;
}

export interface VariableDeleteConflictDetail {
  examples: Array<{
    resourceId: string;
    resourceType: "marker" | "scenario";
  }>;
  notice: string | null;
  referenceCount: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function variableDeleteConflictDetail(
  error: unknown,
): VariableDeleteConflictDetail | null {
  if (!isApiRequestError(error) || error.status.code !== "CONFLICT") {
    return null;
  }
  const detail = error.status.details?.find(
    (candidate) => isRecord(candidate) && "reference_count" in candidate,
  );
  if (!isRecord(detail)) {
    return null;
  }
  const referenceCount = detail.reference_count;
  const examples = detail.examples;
  const notice = detail.notice;
  if (
    typeof referenceCount !== "number" ||
    !Number.isInteger(referenceCount) ||
    referenceCount < 0 ||
    !Array.isArray(examples) ||
    (notice !== null && notice !== undefined && typeof notice !== "string")
  ) {
    return null;
  }
  const parsedExamples: VariableDeleteConflictDetail["examples"] = [];
  for (const example of examples) {
    if (!isRecord(example)) {
      return null;
    }
    const resourceType = example.resource_type;
    const resourceId = example.resource_id;
    if (
      (resourceType !== "marker" && resourceType !== "scenario") ||
      typeof resourceId !== "string"
    ) {
      return null;
    }
    parsedExamples.push({ resourceId, resourceType });
  }
  return {
    examples: parsedExamples,
    notice: typeof notice === "string" ? notice : null,
    referenceCount,
  };
}

export function firstErrorMessage(...errors: unknown[]): string | null {
  for (const error of errors) {
    if (error instanceof Error) {
      return error.message;
    }
  }
  return null;
}
